import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAndroidUrl, getIOSUrl, getMacUrl } from '../utils/storeUrls';
import { storeLinkProps } from '../utils/inAppBrowser';

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 12.64c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-2.99-.79-1.54.02-2.96.9-3.75 2.27-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.39-.92-2.4-3.66zM14.1 5.9c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.67-1.09 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.27z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.6 1.8c-.3.3-.5.8-.5 1.4v17.6c0 .6.2 1.1.5 1.4l.1.1 9.9-9.9v-.2L3.7 1.7l-.1.1zm13.3 13.9-3.3-3.3v-.2l3.3-3.3.1.1 3.9 2.2c1.1.6 1.1 1.7 0 2.3l-3.9 2.2h-.1zm-.1.1L13.4 12 3.6 21.8c.4.4 1 .4 1.7.1l11.5-6.1m0-7.6L5.3 2.1c-.7-.4-1.3-.3-1.7.1L13.4 12l3.4-3.8z" />
  </svg>
);

const buttonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 14px',
  borderRadius: '10px',
  border: '1px solid var(--v2-line)',
  background: 'var(--v2-bg)',
  color: 'var(--v2-ink)',
  textDecoration: 'none',
  lineHeight: 1.15,
  transition: 'border-color 0.15s',
};

interface StoreBadgesProps {
  justify?: React.CSSProperties['justifyContent'];
  style?: React.CSSProperties;
}

/** Direct, crawlable links to every store listing: Mac App Store, App Store and Google Play. */
const StoreBadges: React.FC<StoreBadgesProps> = ({ justify = 'flex-start', style }) => {
  const { t, i18n } = useTranslation();
  const downloadOnThe = t('footer.downloadOnThe', 'Download on the');

  const stores = [
    { prefix: downloadOnThe, label: t('footer.macAppStore', 'Mac App Store'), href: getMacUrl(i18n.language), title: 'Download Notely AI for Mac on the Mac App Store', icon: <AppleIcon /> },
    { prefix: downloadOnThe, label: t('footer.appStore', 'App Store'), href: getIOSUrl(i18n.language), title: 'Download Notely AI for iPhone and iPad on the App Store', icon: <AppleIcon /> },
    { prefix: t('footer.getItOn', 'Get it on'), label: 'Google Play', href: getAndroidUrl(i18n.language), title: 'Download Notely for Android on Google Play', icon: <GooglePlayIcon /> },
  ];

  return (
    <nav
      aria-label={t('footer.getTheApp', 'Get the app')}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: justify, ...style }}
    >
      {stores.map((store) => (
        <a key={store.href} {...storeLinkProps(store.href)} title={store.title} className="store-badge" style={buttonStyle}>
          {store.icon}
          <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <span style={{ fontSize: '10px', color: 'var(--v2-ink-muted)' }}>{store.prefix}</span>
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{store.label}</span>
          </span>
        </a>
      ))}
    </nav>
  );
};

export default StoreBadges;
